/* istanbul ignore file */

import MockAdapter from 'axios-mock-adapter';
import faker from 'faker/locale/en';
import pick from 'lodash/pick';
import times from 'lodash/times';

faker.seed(123);

/*
 * Almost a database here.
 * Tables:
 * - mockCategories
 * - mockPaymentMethods
 * - mockConfigurations
 * - mockOutlets
 */

const mockCategories = [
  { name: 'Cash', code: 'cash' },
  { name: 'EWallet', code: 'e-wallet' },
  { name: 'Integration', code: 'integration' },
  { name: 'EDC', code: 'edc' },
  { name: 'Other', code: 'other' },
  { name: 'Invoice', code: 'invoice' },
];

const createMockPaymentMethod = (() => {
  let nextId = 1;
  const defaults = {
    name: 'Unnamed Method',
    code: 'Unnamed Code',
    isActive: true,
  };

  return function(customProps) {
    const mock = {
      ...defaults,
      id: nextId,
      displayOrder: nextId,
      configurable: true,
      ...customProps,
    };
    nextId++;

    return mock;
  };
})();

const mockPaymentMethods = [
  { name: 'Cash', code: 'cash', categoryCode: 'cash' },
  { name: 'Ovo', code: 'ovo', categoryCode: 'e-wallet' },
  { name: 'LinkAja', code: 'tcash', categoryCode: 'e-wallet' },
  { name: 'Akulaku', code: 'akulaku', categoryCode: 'e-wallet' },
  { name: 'Kredivo', code: 'kredivo', categoryCode: 'e-wallet' },
  { name: 'Dana', code: 'dana', categoryCode: 'e-wallet' },
  { name: 'Gopay', code: 'gopay', categoryCode: 'e-wallet' },
  { name: 'Wechatpay', code: 'wechatpay', categoryCode: 'e-wallet' },
  { name: 'Alipay', code: 'alipay', categoryCode: 'e-wallet' },
  { name: 'Invoice', code: 'invoice', categoryCode: 'invoice' },
  {
    name: 'GoFood',
    code: 'gofood',
    categoryCode: 'integration',
    configurable: false,
  },
  { name: 'EDC BCA', code: 'edc_bca', categoryCode: 'edc' },
  { name: 'EDC BNI', code: 'edc_bni', categoryCode: 'edc' },
  { name: 'EDC BRI', code: 'edc_bri', categoryCode: 'edc' },
  { name: 'EDC CIMB NIAGA', code: 'edc_cimb_niaga', categoryCode: 'edc' },
  { name: 'EDC Mandiri', code: 'edc_mandiri', categoryCode: 'edc' },
  { name: 'Other EDC', code: 'other_edc', categoryCode: 'edc' },
  { name: 'Card', code: 'card', categoryCode: 'other' },
  { name: 'EDC', code: 'edc', categoryCode: 'other' },
  { name: 'Other', code: 'other', categoryCode: 'other' },
  { name: 'Gopay', code: 'go_pay', categoryCode: 'other' },
  { name: 'Online Order', code: 'online_orders', categoryCode: 'other' },
  { name: 'Gift Card', code: 'gift_card', categoryCode: 'other' },
  { name: 'GoFood', code: 'other_gofood', categoryCode: 'other' },
  { name: 'Ovo', code: 'other_ovo', categoryCode: 'other' },
  { name: 'Dana', code: 'other_dana', categoryCode: 'other' },
  { name: 'LinkAja', code: 'other_linkaja', categoryCode: 'other' },
  { name: 'Shopeepay', code: 'other_shopeepay', categoryCode: 'other' },
  { name: 'GrabFood', code: 'other_grabfood', categoryCode: 'other' },
].map(props => createMockPaymentMethod(props));

const mockConfigurations = [
  createMockConfiguration({
    name: 'Default Configuration',
    isDefaultConfiguration: true,
    paymentMethodIds: mockPaymentMethods.map(({ id }) => id),
  }),
  ...times(30, () => createMockConfiguration()),
];
sortConfigurations();

const mockOutlets = [...times(100, () => createMockOutlet())];

// For outlet assignment since we can't mock the /outlet api
export function getMockOutlets() {
  return mockOutlets.map(outlet => ({
    ...outlet,
    paymentConfiguration: pick(
      mockConfigurations.find(({ id }) => id === outlet.paymentConfigurationId),
      ['id', 'name']
    ),
  }));
}

function sortConfigurations() {
  mockConfigurations.sort((a, b) => {
    if (a.isDefaultConfiguration) {
      return -1;
    }
    if (b.isDefaultConfiguration) {
      return 1;
    }

    return a.name.localeCompare(b.name);
  });
}

function randomPaymentMethods() {
  const paymentMethods = [];

  for (let i = 0; i < mockPaymentMethods.length; ++i) {
    // About 75%
    const toInclude = faker.random.boolean() || faker.random.boolean();
    if (toInclude && mockPaymentMethods[i].configurable) {
      paymentMethods.push(mockPaymentMethods[i].id);
    }
  }

  return paymentMethods;
}

function createMockConfiguration(props) {
  return {
    id: faker.random.number(),
    name: `${faker.address.city()} ${faker.commerce.department()}`,
    isDefaultConfiguration: false,
    paymentMethodIds: randomPaymentMethods(),
    ...props,
  };
}

function createMockOutlet(props) {
  const paymentConfiguration = faker.random.arrayElement(mockConfigurations);

  return {
    id: faker.random.number(),
    name: `${faker.address.city()} Outlet ${faker.random.number(100)}`,
    paymentConfigurationId: paymentConfiguration.id,
    ...props,
  };
}

function getConfigurationOutlets(configuration) {
  return mockOutlets.filter(
    ({ paymentConfigurationId }) => paymentConfigurationId === configuration.id
  );
}

// API Doc: https://mokapos.atlassian.net/wiki/spaces/PPTD/pages/248316139/Payment+Configuration+-+Technical+Documentation
export function mockAxios(axiosInstance) {
  const mock = new MockAdapter(axiosInstance, {
    onNoMatch: 'passthrough',
    delayResponse: 200,
  });

  mock
    .onGet(/\/payment-config\/v1\/payment-method-categories$/)
    .reply(function() {
      const result = mockCategories.map(cat => {
        const paymentMethods = mockPaymentMethods
          .filter(({ categoryCode }) => categoryCode === cat.code)
          .map(method => pick(method, ['id', 'code', 'name']));

        return {
          ...cat,
          paymentMethodCodes: paymentMethods,
        };
      });
      return [200, result];
    });

  // We are only interested with configurable payment methods
  mock.onGet(/\/payment-config\/v1\/payment-methods$/).reply(function() {
    const paymentMethods = mockPaymentMethods
      .filter(method => method.configurable)
      .map(method => {
        const result = pick(method, [
          'id',
          'name',
          'code',
          'isActive',
          'displayOrder',
        ]);

        const category = mockCategories.find(
          ({ code }) => code === method.categoryCode
        );
        result.category = pick(category, ['name', 'code']);

        return result;
      });
    return [200, paymentMethods];
  });

  mock.onGet(/\/payment-config\/v1\/configurations$/).reply(function() {
    return [
      200,
      mockConfigurations.map(config => {
        const outlets = getConfigurationOutlets(config);
        return {
          ...pick(config, ['id', 'name', 'isDefaultConfiguration']),
          assignedOutletNum: outlets.length,
        };
      }),
    ];
  });

  mock
    .onGet(/\/payment-config\/v1\/configurations\/outlets$/)
    .reply(function() {
      const result = mockOutlets.reduce((memo, outlet) => {
        memo[outlet.id] = outlet.paymentConfigurationId;
        return memo;
      }, {});

      return [200, result];
    });

  mock
    .onGet(/\/payment-config\/v1\/configurations\/(\d+)$/)
    .reply(function(config) {
      const { url } = config;
      const configurationId = parseInt(url.split('/').pop());

      const configuration = mockConfigurations.find(
        ({ id }) => id === configurationId
      );

      if (!configuration) {
        return [404, {}];
      }

      const outlets = getConfigurationOutlets(configuration);
      return [
        200,
        { ...configuration, outletIds: outlets.map(({ id }) => id) },
      ];
    });

  mock.onPost(/\/payment-config\/v1\/configurations$/).reply(function(config) {
    const { data } = config;
    const props = JSON.parse(data);

    if (!props || !props.name || !props.outletIds || !props.paymentMethodIds) {
      return [
        422,
        {
          message: '`name`, `outletIds`, `paymentMethodIds` are mandatory',
        },
      ];
    }

    const configurationNameExist = mockConfigurations.find(
      ({ name }) => name === props.name
    );
    if (configurationNameExist) {
      return [
        422,
        {
          message: 'Configuration name already exists',
        },
      ];
    }

    const configuration = createMockConfiguration(
      pick(props, 'name', 'paymentMethodIds')
    );
    mockConfigurations.push(configuration);
    sortConfigurations();

    for (const outletId of props.outletIds) {
      const outlet = mockOutlets.find(({ id }) => id === outletId);
      outlet.paymentConfigurationId = configuration.id;
    }

    return [200, { ...configuration, outletIds: props.outletIds }];
  });

  mock
    .onDelete(/\/payment-config\/v1\/configurations\/[^/]+$/)
    .reply(function(config) {
      const { url } = config;
      const configurationId = parseInt(url.split('/').pop());

      const index = mockConfigurations.findIndex(
        ({ id }) => id === configurationId
      );

      if (index < 0) {
        return [404, {}];
      }

      const configuration = mockConfigurations[index];
      mockConfigurations.splice(index, 1);

      const outlets = getConfigurationOutlets(configuration);
      const originalOutletIds = outlets.map(({ id }) => id);
      const defaultConfiguration = mockConfigurations[0];

      for (const outlet of outlets) {
        // eslint-disable-next-line prefer-destructuring
        outlet.paymentConfigurationId = defaultConfiguration.id;
      }

      return [200, { ...configuration, outletIds: originalOutletIds }];
    });

  mock
    .onPut(/\/payment-config\/v1\/configurations\/[^/]+$/)
    .reply(function(config) {
      const { url, data } = config;
      const configurationId = parseInt(url.split('/').pop());

      const index = mockConfigurations.findIndex(
        ({ id }) => id === configurationId
      );

      if (index < 0) {
        return [404, {}];
      }

      const props = JSON.parse(data);
      const configuration = mockConfigurations[index];

      const configurationNameExist = mockConfigurations.find(
        ({ name }) => name === props.name
      );
      if (configurationNameExist) {
        return [
          422,
          {
            message: 'Configuration name already exists',
          },
        ];
      }

      let result = {};
      const outlets = getConfigurationOutlets(configuration);
      if (props.outletIds) {
        const defaultConfiguration = mockConfigurations[0];

        for (const outletId of props.outletIds) {
          const outlet = mockOutlets.find(({ id }) => id === outletId);
          outlet.paymentConfigurationId = configuration.id;
        }

        for (const outlet of outlets) {
          if (props.outletIds.includes(outlet.id)) {
            outlet.paymentConfigurationId = configurationId;
          } else {
            outlet.paymentConfigurationId = defaultConfiguration.id;
          }
        }
        result = { ...configuration, ...props };
      } else {
        result = {
          ...configuration,
          outletIds: outlets.map(({ id }) => id),
          ...props,
        };
      }

      mockConfigurations[index] = result;
      return [200, result];
    });

  return mock;
}
